import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { TrashIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TrashIcon'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { UnstyledIconButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/unstyledIconButton/UnstyledIconButton'
import { ToggleSwitch } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/toggleSwitch/ToggleSwitch'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { hasRecordsPdfAdHocTerm, normalizeRecordsPdfAdHocTerm } from './recordsPdfAdHocTerms'
import { RECORDS_PDF_REDACTION_CATEGORIES } from './recordsPdfRedactionRules'

const RedactionOptionsColumn = styled.View(({ theme: { breakpoints, spacing } }) => ({
  gap: spacing['24px'],
  minWidth: 0,
  ...(!breakpoints.isMobileSized && {
    flex: 1,
    flexBasis: 0,
  }),
}))

const CardHeader = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
}))

const RedactionKeywordsContainer = styled.View(({ theme: { breakpoints, colors, spacing } }) => ({
  gap: spacing['16px'],
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  padding: spacing['24px'],
  border: `1px solid ${colors.borderDefault}`,
  borderRadius: 12,
  boxSizing: 'border-box',
  ...(!breakpoints.isMobileSized && { alignSelf: 'stretch' }),
}))

const RedactionKeywordsListContainer = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['20px'],
  maxHeight: 280,
  minHeight: 0,
  overflow: 'auto',
  paddingRight: spacing['4px'],
}))

const AutomaticRedactionsToggleRow = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: spacing['16px'],
}))

const RedactionCategory = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['12px'],
}))

const RedactionCategoryHeader = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  gap: spacing['8px'],
}))

const RedactionCategoryCheckbox = styled.View({
  flex: 1,
  minWidth: 0,
})

const RedactionCategoryCount = styled(BodyText)(({ theme: { colors } }) => ({
  color: colors.textSecondary,
  flexShrink: 0,
}))

const RedactionCategoryTerms = styled.View<{ $enabled: boolean }>(({ $enabled, theme: { spacing } }) => ({
  gap: spacing['12px'],
  marginLeft: spacing['32px'],
  opacity: $enabled ? 1 : 0.45,
}))

const AdHocRedactionTermsContainer = styled.View<{ $enabled: boolean }>(
  ({ $enabled, theme: { breakpoints, colors, spacing } }) => ({
    gap: spacing['12px'],
    minHeight: 0,
    overflow: 'hidden',
    padding: spacing['24px'],
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 12,
    boxSizing: 'border-box',
    opacity: $enabled ? 1 : 0.45,
    ...(!breakpoints.isMobileSized && { alignSelf: 'stretch' }),
  }),
)

const AdHocRedactionTermsHeader = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['4px'],
}))

const AdHocRedactionTermInputRow = styled.View(({ theme: { breakpoints, spacing } }) => ({
  alignItems: breakpoints.isMobileSized ? 'stretch' : 'flex-end',
  flexDirection: breakpoints.isMobileSized ? 'column' : 'row',
  gap: spacing['12px'],
}))

const AdHocRedactionTermInputContainer = styled.View({
  flex: 1,
  minWidth: 0,
})

const AdHocRedactionTermButtonContainer = styled.View({
  flexShrink: 0,
})

const AdHocRedactionTermsListHeader = styled.View(({ theme: { spacing } }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: spacing['12px'],
}))

const AdHocRedactionTermCount = styled(BodyText)(({ theme: { colors } }) => ({
  color: colors.textSecondary,
}))

const AdHocRedactionTermsList = styled.View(({ theme: { spacing } }) => ({
  gap: spacing['8px'],
  maxHeight: 176,
  minHeight: 0,
  overflow: 'auto',
  paddingRight: spacing['4px'],
}))

const AdHocRedactionTermRow = styled.View(({ theme: { colors, spacing } }) => ({
  alignItems: 'center',
  backgroundColor: colors.backgroundSecondary,
  borderRadius: 8,
  flexDirection: 'row',
  gap: spacing['8px'],
  justifyContent: 'space-between',
  minHeight: 44,
  paddingHorizontal: spacing['12px'],
  paddingVertical: spacing['8px'],
}))

const AdHocRedactionTermText = styled(BodyText)({
  flex: 1,
  minWidth: 0,
})

const RemoveAdHocRedactionTermButton = styled(UnstyledIconButton)(({ theme: { spacing } }) => ({
  padding: spacing['4px'],
}))

type RecordsPdfRedactionOptionsProps = {
  adHocRedactionTerms: readonly string[]
  automaticRedactionsEnabled: boolean
  enabledRedactionCategoryIds: ReadonlySet<string>
  expandedRedactionCategoryIds: ReadonlySet<string>
  selectedRedactionRuleIds: ReadonlySet<string>
  onAddAdHocRedactionTerm: (term: string) => void
  onAutomaticRedactionsChange: (enabled: boolean) => void
  onRedactionCategoryExpansionToggle: (categoryId: string) => void
  onRedactionCategoryToggle: (categoryId: string) => void
  onRedactionRuleToggle: (ruleId: string) => void
  onRemoveAdHocRedactionTerm: (term: string) => void
}

type AutomaticRedactionsCardProps = Pick<
  RecordsPdfRedactionOptionsProps,
  | 'automaticRedactionsEnabled'
  | 'enabledRedactionCategoryIds'
  | 'expandedRedactionCategoryIds'
  | 'selectedRedactionRuleIds'
  | 'onAutomaticRedactionsChange'
  | 'onRedactionCategoryExpansionToggle'
  | 'onRedactionCategoryToggle'
  | 'onRedactionRuleToggle'
>

const AutomaticRedactionsCard: React.FC<AutomaticRedactionsCardProps> = React.memo(
  ({
    automaticRedactionsEnabled,
    enabledRedactionCategoryIds,
    expandedRedactionCategoryIds,
    selectedRedactionRuleIds,
    onAutomaticRedactionsChange,
    onRedactionCategoryExpansionToggle,
    onRedactionCategoryToggle,
    onRedactionRuleToggle,
  }) => {
    const { formatMessage } = useIntl()

    return (
      <RedactionKeywordsContainer>
        <CardHeader>
          <Title
            size={TitleSize.XSMALL}
            text={formatMessage({
              defaultMessage: 'Automatic redactions',
              description: 'Heading for configuring suggested redactions in a client records PDF',
            })}
            testID={tID('ClientRecords-redactionKeywordsTitle')}
          />
          <BodyText
            size={BodyTextSize.SMALL}
            text={formatMessage({
              defaultMessage:
                'Choose which categories and terms should suggest sentences for redaction in the PDF review.',
              description: 'Explanation of how automatic client records PDF redactions are configured',
            })}
            testID={tID('ClientRecords-redactionKeywordsDescription')}
          />
        </CardHeader>
        <AutomaticRedactionsToggleRow>
          <BodyText medium text='Automatically suggest redactions' />
          <ToggleSwitch
            accessibilityLabel={`Automatically suggest redactions, ${automaticRedactionsEnabled ? 'on' : 'off'}`}
            isOn={automaticRedactionsEnabled}
            onPress={onAutomaticRedactionsChange}
            testID={tID('ClientRecords-automaticRedactions')}
          />
        </AutomaticRedactionsToggleRow>
        <RedactionKeywordsListContainer>
          {RECORDS_PDF_REDACTION_CATEGORIES.map((category) => {
            const isExpanded = expandedRedactionCategoryIds.has(category.id)
            const isCategoryEnabled = enabledRedactionCategoryIds.has(category.id)
            const areCategoryTermsEnabled = automaticRedactionsEnabled && isCategoryEnabled
            return (
              <RedactionCategory key={category.id}>
                <RedactionCategoryHeader>
                  <RedactionCategoryCheckbox>
                    <Checkbox
                      baseInputStyle={{ marginBottom: 0 }}
                      checked={isCategoryEnabled}
                      onPress={() => onRedactionCategoryToggle(category.id)}
                      readOnly={!automaticRedactionsEnabled}
                      testID={tID(`ClientRecords-redactionCategory-${category.id}`)}
                      title={category.label}
                    />
                  </RedactionCategoryCheckbox>
                  <RedactionCategoryCount
                    size={BodyTextSize.CAPTION}
                    text={`${category.rules.length} terms`}
                    testID={tID(`ClientRecords-redactionCategoryCount-${category.id}`)}
                  />
                  <UnstyledIconButton
                    accessibilityLabel={`${isExpanded ? 'Collapse' : 'Expand'} ${category.label}, ${category.rules.length} terms`}
                    accessibilityState={{ expanded: isExpanded }}
                    leftIcon={
                      <ChevronIcon
                        direction={isExpanded ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
                        size={20}
                      />
                    }
                    onPress={() => onRedactionCategoryExpansionToggle(category.id)}
                    testID={tID(`ClientRecords-redactionCategoryDisclosure-${category.id}`)}
                  />
                </RedactionCategoryHeader>
                {isExpanded && (
                  <RedactionCategoryTerms $enabled={areCategoryTermsEnabled}>
                    {category.rules.map((rule) => (
                      <Checkbox
                        baseInputStyle={{ marginBottom: 0 }}
                        checked={selectedRedactionRuleIds.has(rule.id)}
                        key={rule.id}
                        labelSize={BodyTextSize.SMALL}
                        onPress={() => onRedactionRuleToggle(rule.id)}
                        readOnly={!areCategoryTermsEnabled}
                        testID={tID(`ClientRecords-redactionRule-${rule.id}`)}
                        title={rule.label}
                      />
                    ))}
                  </RedactionCategoryTerms>
                )}
              </RedactionCategory>
            )
          })}
        </RedactionKeywordsListContainer>
      </RedactionKeywordsContainer>
    )
  },
)

AutomaticRedactionsCard.displayName = 'AutomaticRedactionsCard'

type AdditionalTermsCardProps = Pick<
  RecordsPdfRedactionOptionsProps,
  'adHocRedactionTerms' | 'automaticRedactionsEnabled' | 'onAddAdHocRedactionTerm' | 'onRemoveAdHocRedactionTerm'
>

/**
 * The in-progress term is deliberately local state rather than lifted into ClientRecords: that
 * parent renders the whole export screen, so a keystroke-per-render there was visibly laggy.
 */
const AdditionalTermsCard: React.FC<AdditionalTermsCardProps> = React.memo(
  ({ adHocRedactionTerms, automaticRedactionsEnabled, onAddAdHocRedactionTerm, onRemoveAdHocRedactionTerm }) => {
    const [adHocRedactionTermInput, setAdHocRedactionTermInput] = useState('')
    const [adHocRedactionTermError, setAdHocRedactionTermError] = useState<string>()

    const handleTermInputChange = useCallback((value: string) => {
      setAdHocRedactionTermInput(value)
      setAdHocRedactionTermError(undefined)
    }, [])

    const handleAddTerm = useCallback(() => {
      if (!automaticRedactionsEnabled) return

      const normalizedTerm = normalizeRecordsPdfAdHocTerm(adHocRedactionTermInput)
      if (!normalizedTerm) return

      if (hasRecordsPdfAdHocTerm(adHocRedactionTerms, normalizedTerm)) {
        setAdHocRedactionTermError('This term has already been added.')
        return
      }

      onAddAdHocRedactionTerm(normalizedTerm)
      setAdHocRedactionTermInput('')
      setAdHocRedactionTermError(undefined)
    }, [adHocRedactionTermInput, adHocRedactionTerms, automaticRedactionsEnabled, onAddAdHocRedactionTerm])

    return (
      <AdHocRedactionTermsContainer $enabled={automaticRedactionsEnabled}>
        <AdHocRedactionTermsHeader>
          <BodyText medium text='Additional terms for this export' />
          <BodyText
            size={BodyTextSize.SMALL}
            text='Add a word or phrase to suggest sentences containing it. These terms apply only to this export.'
          />
        </AdHocRedactionTermsHeader>
        <AdHocRedactionTermInputRow>
          <AdHocRedactionTermInputContainer>
            <InputField
              accessibilityLabel='Additional redaction word or phrase'
              autoCapitalize='none'
              baseInputStyle={{ marginBottom: 0 }}
              blurOnSubmit={false}
              error={adHocRedactionTermError}
              label='Word or phrase'
              maxLength={120}
              name='additionalRedactionTerm'
              onChange={(event) => handleTermInputChange(event.nativeEvent.text)}
              onSubmitEditing={handleAddTerm}
              placeholder='Enter a word or phrase'
              readOnly={!automaticRedactionsEnabled}
              returnKeyType='done'
              testID={tID('ClientRecords-adHocRedactionTermInput')}
              value={adHocRedactionTermInput}
            />
          </AdHocRedactionTermInputContainer>
          <AdHocRedactionTermButtonContainer>
            <SecondaryButton
              disabled={!automaticRedactionsEnabled || !normalizeRecordsPdfAdHocTerm(adHocRedactionTermInput)}
              isSmall
              onPress={handleAddTerm}
              testID={tID('ClientRecords-addAdHocRedactionTerm')}
              text='Add term'
            />
          </AdHocRedactionTermButtonContainer>
        </AdHocRedactionTermInputRow>
        <AdHocRedactionTermsListHeader>
          <BodyText medium size={BodyTextSize.SMALL} text='Added terms' />
          <AdHocRedactionTermCount
            size={BodyTextSize.CAPTION}
            text={`${adHocRedactionTerms.length} ${adHocRedactionTerms.length === 1 ? 'term' : 'terms'}`}
          />
        </AdHocRedactionTermsListHeader>
        {adHocRedactionTerms.length ? (
          <AdHocRedactionTermsList>
            {adHocRedactionTerms.map((term) => (
              <AdHocRedactionTermRow key={term}>
                <AdHocRedactionTermText size={BodyTextSize.SMALL} text={term} />
                <RemoveAdHocRedactionTermButton
                  accessibilityLabel={`Remove additional redaction term ${term}`}
                  accessibilityState={{ disabled: !automaticRedactionsEnabled }}
                  disabled={!automaticRedactionsEnabled}
                  leftIcon={<TrashIcon size={20} />}
                  onPress={() => onRemoveAdHocRedactionTerm(term)}
                  testID={tID(`ClientRecords-removeAdHocRedactionTerm-${term}`)}
                />
              </AdHocRedactionTermRow>
            ))}
          </AdHocRedactionTermsList>
        ) : (
          <BodyText size={BodyTextSize.SMALL} text='No additional terms added.' />
        )}
      </AdHocRedactionTermsContainer>
    )
  },
)

AdditionalTermsCard.displayName = 'AdditionalTermsCard'

export const RecordsPdfRedactionOptions: React.FC<RecordsPdfRedactionOptionsProps> = ({
  adHocRedactionTerms,
  automaticRedactionsEnabled,
  enabledRedactionCategoryIds,
  expandedRedactionCategoryIds,
  selectedRedactionRuleIds,
  onAddAdHocRedactionTerm,
  onAutomaticRedactionsChange,
  onRedactionCategoryExpansionToggle,
  onRedactionCategoryToggle,
  onRedactionRuleToggle,
  onRemoveAdHocRedactionTerm,
}) => (
  <RedactionOptionsColumn>
    <AutomaticRedactionsCard
      automaticRedactionsEnabled={automaticRedactionsEnabled}
      enabledRedactionCategoryIds={enabledRedactionCategoryIds}
      expandedRedactionCategoryIds={expandedRedactionCategoryIds}
      onAutomaticRedactionsChange={onAutomaticRedactionsChange}
      onRedactionCategoryExpansionToggle={onRedactionCategoryExpansionToggle}
      onRedactionCategoryToggle={onRedactionCategoryToggle}
      onRedactionRuleToggle={onRedactionRuleToggle}
      selectedRedactionRuleIds={selectedRedactionRuleIds}
    />
    <AdditionalTermsCard
      adHocRedactionTerms={adHocRedactionTerms}
      automaticRedactionsEnabled={automaticRedactionsEnabled}
      onAddAdHocRedactionTerm={onAddAdHocRedactionTerm}
      onRemoveAdHocRedactionTerm={onRemoveAdHocRedactionTerm}
    />
  </RedactionOptionsColumn>
)
